export interface AIAnswerItem {
  q: string;
  a: string;
}

export interface PageAIAnswerProps {
  heading?: string;
  subheading?: string;
  items: AIAnswerItem[];
}

export default function PageAIAnswer({
  heading = 'About Yarnell Emporium',
  subheading,
  items,
}: PageAIAnswerProps) {
  return (
    <section className="bg-charcoal py-24 lg:py-32 overflow-hidden relative">
      {/* Large decorative background text */}
      <div
        className="absolute right-0 top-1/2 -translate-y-1/2 font-serif text-white/3 select-none pointer-events-none leading-none"
        style={{ fontSize: 'clamp(8rem, 18vw, 22rem)', fontWeight: 700, letterSpacing: '-0.04em' }}
        aria-hidden="true"
      >
        FAQ
      </div>

      <div className="max-w-screen-xl mx-auto px-8 lg:px-12 relative">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between mb-16 gap-6">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <span className="w-10 h-px bg-antique" />
              <span className="text-antique text-xs font-sans font-500 tracking-[0.24em] uppercase">
                {subheading || 'AI & Voice Search Ready'}
              </span>
            </div>
            <h2
              className="font-serif text-ivory"
              style={{ fontSize: 'clamp(1.8rem, 3.5vw, 3rem)', fontWeight: 700, lineHeight: 1.1, letterSpacing: '-0.015em' }}
            >
              {heading}
            </h2>
          </div>
          <p className="text-ivory/25 text-xs font-sans leading-relaxed max-w-xs tracking-wide lg:text-right">
            Structured content optimized for AI search engines, voice assistants & Google featured snippets.
          </p>
        </div>

        {/* Q&A Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
          {items.map((item, i) => (
            <div
              key={i}
              className={`border-t border-white/10 p-8 lg:p-10 ${
                i % 2 === 0 ? 'lg:border-r lg:border-white/10' : ''
              }`}
            >
              {/* Question number + question */}
              <div className="flex items-start gap-5 mb-5">
                <span
                  className="font-serif text-antique/40 flex-shrink-0 leading-none"
                  style={{ fontSize: 'clamp(2.5rem, 4vw, 3.5rem)', fontWeight: 700 }}
                >
                  {String(i + 1).padStart(2, '0')}
                </span>
                <h3 className="font-serif text-antique text-lg lg:text-xl font-600 leading-snug pt-2">
                  {item.q}
                </h3>
              </div>
              <p className="text-ivory/50 text-sm font-sans font-300 leading-relaxed pl-16">
                {item.a}
              </p>
            </div>
          ))}
        </div>

        {/* Bottom rule */}
        <div className="mt-0 border-t border-white/10 pt-8 flex items-center justify-between">
          <span className="text-ivory/20 text-xs font-sans tracking-widest uppercase">
            Yarnell Emporium — Antiques · Furniture · Gifts · Apparel
          </span>
          <a
            href="tel:9284279593"
            className="inline-flex items-center gap-2 text-antique text-xs font-sans font-600 tracking-[0.14em] uppercase hover:text-white transition-colors cursor-pointer"
          >
            <i className="ri-phone-line" /> 928.427-9593
          </a>
        </div>
      </div>
    </section>
  );
}
